import React, { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
    InsufficientPermissions,
    PaginatedTable,
    PaginatedTableCallbacks,
    useTableUrlSync,
} from '../../common'
import { SupplierBalancesTable } from './collectionTable'
import { JsonSupplierBalance, SupplierBalance } from '../../../models'
import { getSupplierBalances } from '../../../api'
import { downloadSupplierBalancesCsv } from '../../../api/routes'
import defineAbility from '../defineAbility'
import { getAuthUser } from '../../../helpers'

export const SupplierBalanceLanding = () => {
    const ability = defineAbility(getAuthUser())
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const [error, setError] = useState<string | null>(null)
    const minInputRef = useRef<HTMLInputElement | null>(null)
    const maxInputRef = useRef<HTMLInputElement | null>(null)

    const [searchParams, setSearchParams] = useSearchParams()
    const tableUrlSync = useTableUrlSync(['minimumBalance', 'maximumBalance'])
    const sortBy = searchParams.get('sortBy') || 'balance'
    const sortDirection = searchParams.get('sortDirection') || 'desc'
    const urlMinimumBalance = tableUrlSync.defaultFilters.minimumBalance || ''
    const urlMaximumBalance = tableUrlSync.defaultFilters.maximumBalance || ''

    const handleSortChange = (field: string) => {
        const newDirection =
            sortBy === field ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc'
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set('sortBy', field)
        newSearchParams.set('sortDirection', newDirection)
        setSearchParams(newSearchParams, { replace: true })
    }

    if (!ability.can('View', 'SupplierBalances')) {
        return <InsufficientPermissions />
    }

    const formatNumberWithCommas = (value: string) => {
        const numericValue = value.replace(/[^0-9.]/g, '')
        return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    const handleInputChange = (
        input: HTMLInputElement,
        filterName: string,
        updateFilters: (filterName: string, newValue: string) => void
    ) => {
        const formattedValue = formatNumberWithCommas(input.value)
        input.value = formattedValue

        const numericValue = formattedValue.replace(/,/g, '')

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            const minInput = minInputRef.current
            const maxInput = maxInputRef.current
            const min = parseFloat(
                (filterName === 'minimumBalance'
                    ? numericValue
                    : minInput?.value.replace(/,/g, '')) || '0'
            )
            const max = parseFloat(
                (filterName === 'maximumBalance'
                    ? numericValue
                    : maxInput?.value.replace(/,/g, '')) || '0'
            )

            if (min && max && min > max) {
                setError('Min balance cannot be larger than max balance')
                return
            }

            setError(null)

            updateFilters(filterName, numericValue)

            const newSearchParams = new URLSearchParams(searchParams)
            if (numericValue) {
                newSearchParams.set(filterName, numericValue)
            } else {
                newSearchParams.delete(filterName)
            }
            setSearchParams(newSearchParams)
        }, 500)
    }

    return (
        <PaginatedTable<SupplierBalance, JsonSupplierBalance>
            getData={(params) =>
                getSupplierBalances({
                    ...params,
                    ...(sortBy ? { sortBy } : {}),
                    ...(sortDirection ? { sortDirection } : {}),
                })
            }
            modelConstructor={(json: JsonSupplierBalance) => new SupplierBalance(json)}
            searchPlaceholder="Search by Supplier Name, Phone, or Fax"
            defaultSearch={tableUrlSync.defaultSearch}
            defaultPage={tableUrlSync.defaultPage}
            persistSearch={tableUrlSync.persistSearch}
            onParamsChange={tableUrlSync.onParamsChange}
            defaultFilters={tableUrlSync.defaultFilters}
            Filters={({ updateFilters }) => (
                <div className="row mb-2">
                    <div className="col">
                        <label htmlFor="minimumBalance">Min Amount</label>
                        <input
                            id="minimumBalance"
                            type="text"
                            className="form-control"
                            ref={minInputRef}
                            defaultValue={formatNumberWithCommas(urlMinimumBalance)}
                            placeholder="0"
                            onChange={(e) =>
                                handleInputChange(e.target, 'minimumBalance', updateFilters)
                            }
                        />
                    </div>
                    <div className="col">
                        <label htmlFor="maximumBalance">Max Amount</label>
                        <input
                            id="maximumBalance"
                            type="text"
                            className="form-control"
                            ref={maxInputRef}
                            defaultValue={formatNumberWithCommas(urlMaximumBalance)}
                            placeholder="0"
                            onChange={(e) =>
                                handleInputChange(e.target, 'maximumBalance', updateFilters)
                            }
                        />
                    </div>
                    {error && <div className="text-danger mt-2">{error}</div>}
                </div>
            )}
            Table={(
                supplierBalances: SupplierBalance[],
                callbacks: PaginatedTableCallbacks
            ) => (
                <SupplierBalancesTable
                    callbacks={callbacks}
                    resources={{ supplierBalances }}
                />
            )}
            title="Supplier Balances"
            DownloadButton={({ params }) => {

                const downloadSearchParams = new URLSearchParams(searchParams)

                for (const [key, value] of Object.entries(params)) {

                    if (value != null && value !== '') {
                        downloadSearchParams.set(key, String(value))
                    }
                }
                downloadSearchParams.set('sortBy', sortBy)
                downloadSearchParams.set('sortDirection', sortDirection)
                return (
                <a href={`${downloadSupplierBalancesCsv}?${downloadSearchParams.toString()}`}
                    className="btn btn-primary"
                    download="SupplierBalances.csv">
                    Download CSV
                </a>
            )
        }}
            sortOptions={{
                sortBy,
                sortDirection,
                onSortChange: handleSortChange,
                sortFields: [
                    { field: 'name', label: 'Name' },
                    { field: 'balance', label: 'Balance' },
                ],
            }}
        />
    )
}
